import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom";
import { DoctorCard } from "../../components/doctor-card"
import SEO from "../SEO";
import {
  Calendar,
  Phone,
  ChevronRight,
  Heart,
  HeartPulse,
  Clock,
  CheckCircle,
  MessageSquare,
  User,
  Star,
  PlusCircle,
  Activity,
  Stethoscope,
  Zap,
} from "lucide-react"
 
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardFooter } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion"
 
// ==========================
//  WP API URL
// ==========================
const API = "/wp-json/wp/v2"
 
export default function Servicesdetail() {
  const { slug } = useParams()
  const [loading, setLoading] = useState(true)
  const [service, setService] = useState(null)
  const [post, setPost] = useState(null)
  const [testimonials, setTestimonials] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [hospitalSpecId, setHospitalSpecId] = useState(null);
  const [hospitalSpecName, setHospitalSpecName] = useState("");
  const navigate = useNavigate();
 
 
  // ==========================================
  // Fetch category → posts → ACF fields
  // ==========================================
 
  async function loadService() {
    try {
      // 1️⃣ Get category by slug
      const catRes = await fetch(`${API}/categories?slug=${slug}`);
      const catData = await catRes.json();
 
      // ❌ If no category found → go to 404
      if (!catData || !catData.length) {
        navigate("/404", { replace: true });
        return;
      }
 
      const category = catData[0];
      setService(category);
      setHospitalSpecName(category.name); // Default to category name
 
      // Look up the correct hospital API specialization ID by name
      // try {
      //   const specRes = await fetch('/apis/web/specializations')
      //   const specData = await specRes.json()
      //   const specList = Array.isArray(specData) ? specData : specData.result || []
      //   const catName = category.name.toLowerCase()
      //   const match = specList.find(s =>
      //     s.title && (
      //       s.title.toLowerCase().includes(catName) ||
      //       catName.includes(s.title.toLowerCase())
      //     )
      //   )
      //   if (match) {
      //     setHospitalSpecId(match.id)
      //     setHospitalSpecName(match.title) // Use the HIS title for better matching
      //   }
      // } catch (e) {}
 
      // 2️⃣ Get the main service post
      const postRes = await fetch(
        `${API}/posts?categories=${category.id}&_embed&yoast_head_json`
      );
      const posts = await postRes.json();
 
      // ❌ If no posts found → go to 404
      if (!posts || !posts.length) {
        navigate("/404", { replace: true });
        return;
      }
 
      setPost(posts[0]);

      // Prioritize the specialization ID from the CMS (ACF) if it exists
      if (posts[0].acf?.specialization_id) {
        setHospitalSpecId(posts[0].acf.specialization_id);
      }
      // Prioritize the booking search name from the CMS (ACF) if it exists
      if (posts[0].acf?.booking_search_name) {
        setHospitalSpecName(posts[0].acf.booking_search_name);
      }
 
      // 3️⃣ Fetch Testimonials
      const testiRes = await fetch(
        `${API}/testimonials?categories=${category.id}&per_page=3`
      );
      const testiData = await testiRes.json();
      setTestimonials(testiData || []);
 
    const docRes = await fetch(
      `/wp-json/custom/v1/doctors?service=${slug}`
    );
      const docData = await docRes.json();
      const filteredDocs = (docData || []).filter(d => d.doctor_id);

      // Fetch real WP slugs using the IDs from the custom API
      if (filteredDocs.length > 0) {
        const ids = filteredDocs.map(d => d.id).join(",");
        const slugRes = await fetch(`/wp-json/wp/v2/doctors?include=${ids}&_fields=id,slug&per_page=100`);
        const slugData = await slugRes.json();
        const slugMap = {};
        slugData.forEach(d => { slugMap[d.id] = d.slug; });
        setDoctors(filteredDocs.map(d => ({ ...d, slug: slugMap[d.id] || "" })));
      } else {
        setDoctors([]);
      }
 
      setLoading(false);
 
    } catch (error) {
      console.log("API Error:", error);
 
      // ❌ On ANY API error → go to 404
      navigate("/404", { replace: true });
    }
  }
 
 
  useEffect(() => {
    loadService()
  }, [slug])
 
 
 
  if (loading) return <div className="p-10 text-center">Loading...</div>
  if (!post) return <div className="p-10 text-center">Service Not Found</div>
 
  const acf = post.acf || {}
  const toWebp = (url) => (url && typeof url === "string" ? url.replace(/\.(png|jpe?g|gif|bmp|tiff?)$/i, ".webp") : url)

  const seo = post?.seo || {};
  const catSeo = service?.seo || {};
 
  // ----------------------------------------------------
  // YOU CAN MAP ACF FIELDS HERE (match your WP structure)
  // ----------------------------------------------------
 
  const serviceSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://pranaamhospitals.com/#website",
        "url": "https://pranaamhospitals.com/",
        "name": "Pranaam Hospitals"
      },
      {
        "@type": "Hospital",
        "@id": "https://pranaamhospitals.com/#hospital",
        "name": "Pranaam Hospitals",
        "url": "https://pranaamhospitals.com/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pranaamhospitals.com/logo.png"
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Hyderabad",
          "addressRegion": "Telangana",
          "addressCountry": "IN"
        },
        "medicalSpecialty": {
          "@type": "MedicalSpecialty",
          "name": service?.name
        }
      },
      {
        "@type": "WebPage",
        "@id": seo?.canonical || catSeo?.canonical,
        "url": seo?.canonical || catSeo?.canonical,
        "name": seo?.title || post?.title?.rendered,
        "description": seo?.description || acf?.hero_description,
        "inLanguage": "en",
        "isPartOf": {
          "@id": "https://pranaamhospitals.com/#website"
        },
        "about": {
          "@type": "MedicalSpecialty",
          "name": service?.name
        },
        "publisher": {
          "@id": "https://pranaamhospitals.com/#hospital"
        }
      }
    ]
  };
 
 
  const faqSchema =
    acf?.faq_repeater?.length > 0
      ? {
          "@type": "FAQPage",
          "mainEntity": acf.faq_repeater.map((item) => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        }
      : null;
 
 
 
  const finalSchema = faqSchema
  ? {
      "@context": "https://schema.org",
      "@graph": [...serviceSchema["@graph"], faqSchema]
    }
  : serviceSchema;
 
 
 
  return (
    <>
 
    <SEO
  title={seo?.title || catSeo?.title || post?.title?.rendered}

  description={
    seo?.description ||
    catSeo?.description ||
    acf.hero_description
  }

  canonical={
    seo?.canonical ||
    catSeo?.canonical ||
    `https://pranaamhospitals.com/services/${slug}`
  }

  robots={
    Array.isArray(seo?.robots)
      ? seo.robots.join(", ")
      : seo?.robots || "index, follow"
  }

  keywords={acf.key_words || post.title.rendered}

  author="Pranaam Hospitals"

  publisher="Pranaam Hospitals"

  ogTitle={seo?.og_title || catSeo?.og_title}

  ogDescription={
    seo?.og_description ||
    catSeo?.og_description ||
    acf.hero_description
  }

  ogImage={
    seo?.og_image ||
    catSeo?.og_image ||
    seo?.featured_image ||
    acf.hero_image
  }

  twitterTitle={
    seo?.twitter_title ||
    catSeo?.twitter_title
  }

  twitterDescription={
    seo?.twitter_description ||
    catSeo?.twitter_description
  }

  schema={finalSchema}
/>
 
 
    <main className="min-h-screen bg-white">
 
      {/* ============================================
          HERO SECTION (Dynamic)
      ============================================ */}
      <section className="relative py-16 md:py-24 bg-gradient-to-r from-primary/5 to-primary/10">
        <div className="container px-4 mx-auto">
          <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
 
            {/* Dynamic Content */}
            <div className="max-w-lg">
              <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
              {acf.tag_icon && (
                <img className="w-4 h-4 mr-1" src={toWebp(acf.tag_icon)} alt={acf.tag_title || service.name} title={acf.tag_title || service.name} />
              )}
                {acf.tag_title || service.name}
              </Badge>
 
              <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl font-heading">
                {acf.hero_title || post.title.rendered}
              </h1>
 
              <p className="mb-8 text-lg text-gray-700">
                {acf.hero_description}
              </p>
 
              <div className="flex flex-wrap gap-4">
                <Button size="lg" variant="outline" className="gap-2 shadow-md font-heading border-primary text-primary hover:bg-primary hover:text-white bg-transparent" asChild>
                  <Link
                    to={hospitalSpecId
                      ? `/book-now?search=${encodeURIComponent(hospitalSpecName?.toUpperCase())}&specializationId=${hospitalSpecId}&type=SPECIALITY`
                      : `/book-now`
                    }
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Book an Appointment
                  </Link>
                </Button>
              </div>
            </div>
 
            {/* Banner Image */}
            <div className="relative">
              <div className="relative h-64 overflow-hidden rounded-lg shadow-xl md:h-96">
                <img
                  src={(acf.hero_image || "/placeholder.svg").replace(/\.(png|jpe?g|gif|bmp|tiff?)$/i, ".webp")}
                  alt={acf.tag_title || service?.name}
                  width={1200}
                  height={600}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  className="object-cover w-full h-full"
                />
              </div>
            </div>
 
          </div>
        </div>
      </section>
 
      {/* ============================================
          WHY CHOOSE US (ACF Repeater)
      ============================================ */}
      {acf.title?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
 
            <div className="max-w-3xl mx-auto mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
                {acf.tag_icon && (
                    <img className="w-4 h-4 mr-1" src={toWebp(acf.tag_icon)} alt={acf.tag_title || service.name} title={acf.tag_title || service.name} />
                )}  
                <span>Why Choose Us</span>
              </div>
              <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
                {acf.title || "Why Choose Us"}
              </h2>
 
              <p className="text-lg text-gray-700">{acf.description}</p>
            </div>
 
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {acf.why_choose_card_repeater.map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-full bg-primary/10">
                          <img className="w-4 h-4" src={toWebp(item.icon) || "/placeholder.svg?height=24&width=24&text=No+Image"} alt={item.title} title={item.title} />
                        </div>
                        <h3 className="text-xl font-bold font-heading">{item.title}</h3>
                    </div>
                    <p className="text-gray-700">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {acf.service_caption && (
            <div className="p-6 mt-10 text-center bg-primary/5 rounded-lg">
              <p className="text-lg text-gray-700">
                {acf.service_caption || "Why Choose Us"}
              </p>
            </div>
            )}
          </div>
        </section>
      )}
 
      {/* ============================================
          TABS: Diagnostics & Treatments (Dynamic)
      ============================================ */}
      <section id="treatments" className="py-16 bg-gray-50">
        <div className="container px-4 mx-auto">
 
          <div className="max-w-3xl mx-auto mb-12 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
                <Stethoscope className="w-5 h-5" />
                <span>Our Services</span>
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
              {acf.tab_title || "Services & Treatments"}
            </h2>
            <p className="text-lg text-gray-700">
                {acf.tab_description || "Services & Treatments"}
              </p>
          </div>
 
          <Tabs key={acf.tab_repeater?.[0]?.tab_heading || ""}
            defaultValue={
              acf.tab_repeater?.[0]?.tab_heading
                ?.replace(/\s+/g, "-")
                .toLowerCase() || ""
            }
            className="w-full"
            >
 
            {/* TAB HEADERS */}
                <TabsList className="grid w-full grid-cols-2 mb-8 bg-gray-200">
                  {(acf.tab_repeater || []).map((tab, idx) => (
                <TabsTrigger
                    key={idx}
                    value={tab.tab_heading.replace(/\s+/g, "-").toLowerCase()}
                    className="
                      font-heading
                      text-gray-700
                      data-[state=active]:bg-white
                      data-[state=active]:text-black"
                >
                    {tab.tab_heading}
                </TabsTrigger>
                ))}
            </TabsList>
 
 
            {/* TAB CONTENT SECTIONS */}
            {(acf.tab_repeater || []).map((tab, tabIndex) => (
                <TabsContent
                  key={tabIndex}
                  value={tab.tab_heading.replace(/\s+/g, "-").toLowerCase()}
                >
               
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
 
                    {(tab.tab_details || []).map((item, cardIndex) => (
                    <Card key={cardIndex}>
                        <CardContent className="p-6">
 
                        {/* ICON + TITLE */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 rounded-full bg-primary/10">
                            <img
                                className="w-6 h-6"
                                src={toWebp(item.tab_card_icon) || "/placeholder.svg?height=24&width=24&text=No+Image"}
                                alt={item.tab_card_title}
                                title={item.tab_card_title}
                            />
                            </div>
 
                            <h3 className="text-xl font-bold font-heading">
                            {item.tab_card_title}
                            </h3>
                        </div>
 
                        {/* DESCRIPTION */}
                        <p className="mb-4 text-gray-700">
                            {item.tab_card_description}
                        </p>
 
                        {/* BULLET POINTS */}
                        <ul className="space-y-2">
                            {(item.tab_card_bullet_point_reapeater || []).map((bullet, bIndex) => (
                            <li key={bIndex} className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                                <span>{bullet.tab_card_bulletpoint}</span>
                            </li>
                            ))}
                        </ul>
 
                        </CardContent>
                    </Card>
                    ))}
 
                </div>
 
                </TabsContent>
            ))}
 
            </Tabs>
        </div>
      </section>
 
      {/* Meet Our Specialists */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto mb-12 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
              <User className="w-5 h-5" />
              <span>Our Specialists</span>
            </div>
 
            <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
              Meet Our {service?.name}
            </h2>
 
            <p className="text-lg text-gray-700">
              Our team of experienced specialists is dedicated to providing advanced and compassionate care.
            </p>
          </div>
 
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {doctors?.map((doctor) => (
              <DoctorCard
                key={doctor.id}
                name={doctor.name}
                specialty={doctor.designation}
                qualifications={doctor.qualifications}
                image={(doctor.image || "/placeholder.svg?height=400&width=400").replace(/\.(png|jpe?g|gif|bmp|tiff?)$/i, ".webp")}
                href={`/doctors/${doctor.slug}`}
                hospitalDoctorId={doctor.doctor_id}
              />
            ))}
 
          </div>
 
          {/* View More */}
          <div className="flex justify-center mt-8">
            <Button variant="outline" className="gap-2 font-heading" asChild>
              <Link
                  to={hospitalSpecId
                    ? `/book-now?search=${encodeURIComponent(service?.name)}&specializationId=${hospitalSpecId}&type=SPECIALITY`
                    : `/book-now`
                  }
                >  
                View All {service?.name} Specialists
                <ChevronRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
 
        </div>
      </section>
 
      {/* Patient Testimonials */}
      {/* <section className="py-16 bg-gray-50">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto mb-12 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
              <MessageSquare className="w-5 h-5" />
              <span>Testimonials</span>
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">Patient Success Stories</h2>
            <p className="text-lg text-gray-700">
              Read about the experiences of patients who have received {acf.hero_title || post.title.rendered} at Pranaam Hospitals.
            </p>
          </div>
 
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {testimonials?.map((item) => (
              <Card key={item.id} className="transition-all hover:shadow-md">
                <CardContent className="p-6"> */}
 
                  {/* ⭐ Rating */}
                  {/* <div className="flex mb-4">
                    {[...Array(item.acf?.patient_rating || 5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-primary fill-primary" />
                    ))}
                  </div> */}
 
                  {/* ⭐ Review */}
                  {/* <p className="mb-6 italic">
                    &ldquo;{item.acf?.patient_review}&rdquo;
                  </p> */}
 
                  {/* ⭐ Author */}
                  {/* <div className="flex items-center">
                    <div className="relative w-12 h-12 mr-4 overflow-hidden rounded-full">
                      <img
                        src={item.acf?.patient_image || "/placeholder.svg"}
                        alt={item.acf?.patient_name}
                        className="object-cover w-full h-full"
                      />
                    </div>
 
                    <div>
                      <h4 className="font-bold">{item.acf?.patient_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {item.acf?.treatment_related_}
                      </p>
                    </div>
                  </div>
 
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}
 
      {/* ============================================
          FAQ SECTION (ACF Repeater)
      ============================================ */}
       {acf.faq_repeater?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
 
            <div className="max-w-3xl mx-auto mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 rounded-full bg-primary/10 text-primary">
                <span>FAQs</span>
              </div>
 
              <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
                Frequently Asked Questions
              </h2>
 
              <p className="text-lg text-gray-700">
                {acf.description}
              </p>
            </div>
 
            <div className="max-w-3xl mx-auto">
 
              {acf.faq_repeater?.map((item, index) => (
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-left font-heading">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                  </Accordion>
              ))}
 
            </div>
 
          </div>
        </section>
 
      )}
 
    </main>
  </>
  )
}